import axios from "axios";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const apiUrlTipos = `${(process.env.REACT_APP_API_PLATOS || 'http://localhost:8085/api/platos/').replace(/\/+$/, '')}/../tipos-plato`;

const AbmTiposPlato = () => {
    const [tipos, setTipos] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        getTipos();
    }, []);

    const getTipos = async () => {
        try {
            setLoading(true);
            const response = await axios.get(apiUrlTipos);
            setTipos(response.data);
        } catch (error) {
            console.error("Error al obtener tipos de plato:", error);
            alert("Error al cargar los tipos de plato");
        } finally {
            setLoading(false);
        }
    };

    const cambiarEstado = async (id, nuevoEstado) => {
        try {
            await axios.put(`${apiUrlTipos}/${id}`, { activo: nuevoEstado });
        } catch (error) {
            console.error("Error al cambiar estado:", error);
            alert("Error al cambiar el estado del tipo de plato");
            getTipos();
        }
    };

    const handleEliminarPermanente = async (id, nombre) => {
        if (!window.confirm(`¿Seguro que deseas eliminar permanentemente "${nombre}"? Esta acción no se puede deshacer y puede afectar platos que usen este tipo.`)) {
            return;
        }

        try {
            await axios.delete(`${apiUrlTipos}/${id}/permanente`);
            alert("✅ Tipo de plato eliminado permanentemente");
            getTipos();
        } catch (error) {
            console.error("Error al eliminar tipo:", error);
            const mensaje = error.response?.data?.message || "Error al eliminar el tipo de plato";
            alert(`❌ ${mensaje}`);
        }
    };

    const onDragEnd = (result) => {
        const { source, destination, draggableId } = result;

        if (!destination ||
            (source.droppableId === destination.droppableId && source.index === destination.index)) {
            return;
        }

        const nuevoEstado = destination.droppableId === 'activos';
        const tipoId = parseInt(draggableId);

        setTipos(prevTipos =>
            prevTipos.map(tipo =>
                tipo.id_tipo === tipoId
                    ? { ...tipo, activo: nuevoEstado }
                    : tipo
            )
        );

        cambiarEstado(tipoId, nuevoEstado);
    };

    const tiposActivos = tipos.filter(t => t.activo);
    const tiposInactivos = tipos.filter(t => !t.activo);

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 py-8 px-4">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border-b-4 border-purple-500">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                        <div className="flex items-center gap-4">
                            <div className="bg-gradient-to-br from-purple-500 to-pink-600 p-4 rounded-xl shadow-lg">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-800">Tipos de Plato</h1>
                                <p className="text-gray-600 mt-1">Gestiona las categorías de platos</p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => navigate('/admin/crear-tipo-plato')}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Nuevo Tipo
                            </button>
                            <button
                                onClick={() => navigate('/admin/AbmPlatos')}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold shadow-md transition-all"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                Volver
                            </button>
                        </div>
                    </div>
                </div>

                {/* Info box */}
                <div className="mb-6 bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg shadow-md">
                    <div className="flex items-start gap-3">
                        <svg className="w-6 h-6 text-purple-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div>
                            <p className="text-sm font-semibold text-purple-900">Arrastra y suelta para cambiar estado</p>
                            <p className="text-sm text-purple-700 mt-1">
                                Mueve los tipos entre columnas para activarlos o desactivarlos. Solo los activos aparecen al crear platos.
                            </p>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                        <div className="flex items-center justify-center gap-3">
                            <svg className="animate-spin h-8 w-8 text-purple-600" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            <span className="text-lg font-semibold text-gray-700">Cargando tipos de plato...</span>
                        </div>
                    </div>
                ) : (
                    <DragDropContext onDragEnd={onDragEnd}>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Columna ACTIVOS */}
                            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-green-200">
                                <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4">
                                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        Activos ({tiposActivos.length})
                                    </h3>
                                    <p className="text-green-50 text-sm mt-1">
                                        Disponibles al crear platos
                                    </p>
                                </div>
                                <Droppable droppableId="activos">
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            className={`p-4 min-h-[400px] transition-colors ${snapshot.isDraggingOver ? 'bg-green-50' : 'bg-gray-50'}`}
                                        >
                                            {tiposActivos.length === 0 ? (
                                                <div className="text-center text-gray-400 py-12">
                                                    <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                                    </svg>
                                                    <p className="text-lg font-semibold">No hay tipos activos</p>
                                                    <p className="text-sm mt-2">Arrastra desde Inactivos o crea uno nuevo</p>
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    {tiposActivos.map((tipo, index) => (
                                                        <Draggable
                                                            key={tipo.id_tipo}
                                                            draggableId={String(tipo.id_tipo)}
                                                            index={index}
                                                        >
                                                            {(provided, snapshot) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    className={`bg-white rounded-xl p-4 shadow-md hover:shadow-xl transition-all border-2 border-green-200 cursor-move ${snapshot.isDragging ? 'rotate-2 shadow-2xl scale-105 ring-4 ring-green-400/50' : ''}`}
                                                                    style={provided.draggableProps.style}
                                                                >
                                                                    <div className="flex justify-between items-start mb-2">
                                                                        <div className="flex items-center gap-3">
                                                                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                                                            </svg>
                                                                            <h4 className="text-gray-800 font-bold text-lg">{tipo.nombre}</h4>
                                                                        </div>
                                                                        <button
                                                                            onClick={() => navigate(`/admin/editar-tipo-plato/${tipo.id_tipo}`)}
                                                                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                                                                        >
                                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                            </svg>
                                                                            Editar
                                                                        </button>
                                                                    </div>
                                                                    <p className="text-gray-600 text-sm ml-8">
                                                                        {tipo.descripcion || 'Sin descripción'}
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    ))}
                                                </div>
                                            )}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </div>

                            {/* Columna INACTIVOS */}
                            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-red-200">
                                <div className="bg-gradient-to-r from-red-500 to-rose-600 px-6 py-4">
                                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        Inactivos ({tiposInactivos.length})
                                    </h3>
                                    <p className="text-red-50 text-sm mt-1">
                                        No aparecen al crear platos
                                    </p>
                                </div>
                                <Droppable droppableId="inactivos">
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            className={`p-4 min-h-[400px] transition-colors ${snapshot.isDraggingOver ? 'bg-red-50' : 'bg-gray-50'}`}
                                        >
                                            {tiposInactivos.length === 0 ? (
                                                <div className="text-center text-gray-400 py-12">
                                                    <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <p className="text-lg font-semibold">¡Perfecto!</p>
                                                    <p className="text-sm mt-2">Todos los tipos están activos</p>
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    {tiposInactivos.map((tipo, index) => (
                                                        <Draggable
                                                            key={tipo.id_tipo}
                                                            draggableId={String(tipo.id_tipo)}
                                                            index={index}
                                                        >
                                                            {(provided, snapshot) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    className={`bg-white rounded-xl p-4 shadow-md hover:shadow-xl transition-all border-2 border-red-200 opacity-60 hover:opacity-90 cursor-move ${snapshot.isDragging ? 'rotate-2 shadow-2xl scale-105 ring-4 ring-red-400/50' : ''}`}
                                                                    style={provided.draggableProps.style}
                                                                >
                                                                    <div className="flex justify-between items-start mb-2">
                                                                        <div className="flex items-center gap-3">
                                                                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                                                            </svg>
                                                                            <h4 className="text-gray-800 font-bold text-lg">{tipo.nombre}</h4>
                                                                        </div>
                                                                        <div className="flex gap-2">
                                                                            <button
                                                                                onClick={() => navigate(`/admin/editar-tipo-plato/${tipo.id_tipo}`)}
                                                                                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                                                                            >
                                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                                </svg>
                                                                                Editar
                                                                            </button>
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleEliminarPermanente(tipo.id_tipo, tipo.nombre);
                                                                                }}
                                                                                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                                                                            >
                                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                </svg>
                                                                                Eliminar
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    <p className="text-gray-600 text-sm ml-8">
                                                                        {tipo.descripcion || 'Sin descripción'}
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    ))}
                                                </div>
                                            )}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </div>
                        </div>
                    </DragDropContext>
                )}

                {/* Info footer */}
                <div className="mt-6 bg-white border border-gray-200 rounded-xl p-6 shadow-md">
                    <div className="flex items-start gap-3">
                        <svg className="w-6 h-6 text-gray-500 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div>
                            <h3 className="font-bold text-gray-800 mb-2">Información útil</h3>
                            <ul className="space-y-1 text-sm text-gray-600">
                                <li className="flex items-start gap-2">
                                    <span className="text-purple-500 mt-0.5">•</span>
                                    <span>Los tipos activos aparecen en el dropdown al crear o editar platos</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-purple-500 mt-0.5">•</span>
                                    <span>Arrastra un tipo entre columnas para activarlo o desactivarlo</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-purple-500 mt-0.5">•</span>
                                    <span>Los platos existentes con tipos inactivos seguirán funcionando</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-purple-500 mt-0.5">•</span>
                                    <span>Edita el nombre y descripción usando el botón "Editar"</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AbmTiposPlato;
